import {
  OPERATORS,
  SEVERITIES,
  type CreateRuleDefinitionDto,
  type DiscoveredMetricDto,
  type Operator,
  type RuleDefinitionDto,
  type RuleTargetDto,
  type Severity,
  type UpdateRuleDefinitionDto,
} from '@citilight-monitor/contracts';
import { useEffect, useMemo, useState } from 'react';
import { ApiError, useDiscoveredMetrics } from '../../lib/api';
import { PopupTemplate } from '../templates';
import type { FormFieldConfig } from './FormBuilder';
import { FormBuilder } from './FormBuilder';

interface RuleFormValues extends Record<string, string> {
  name: string;
  kind: string;
  ruleTargetId: string;
  metricKey: string;
  operator: string;
  threshold: string;
  consecutive: string;
  severity: string;
  remindEveryMinutes: string;
  enabled: string;
}

const EMPTY_VALUES: RuleFormValues = {
  name: '',
  kind: '',
  ruleTargetId: '',
  metricKey: '',
  operator: '<',
  threshold: '',
  consecutive: '1',
  severity: 'critical',
  remindEveryMinutes: '',
  enabled: 'true',
};

function toValues(rule: RuleDefinitionDto, targets: RuleTargetDto[]): RuleFormValues {
  const target = targets.find((t) => t.id === rule.ruleTargetId);
  return {
    name: rule.name ?? '',
    kind: target?.kind ?? 'SERVER',
    ruleTargetId: rule.ruleTargetId,
    metricKey: rule.metricKey,
    operator: rule.operator,
    threshold: String(rule.threshold),
    consecutive: String(rule.consecutive),
    severity: rule.severity,
    remindEveryMinutes: rule.remindEveryMinutes !== undefined ? String(rule.remindEveryMinutes) : '',
    enabled: rule.enabled ? 'true' : 'false',
  };
}

interface RuleFormDialogProps {
  open: boolean;
  onClose: () => void;
  targets: RuleTargetDto[];
  rule?: RuleDefinitionDto;
  onSubmit: (dto: CreateRuleDefinitionDto | UpdateRuleDefinitionDto) => Promise<unknown>;
}

export function RuleFormDialog({ open, onClose, targets, rule, onSubmit }: RuleFormDialogProps) {
  const [values, setValues] = useState<RuleFormValues>(rule ? toValues(rule, targets) : EMPTY_VALUES);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setValues(rule ? toValues(rule, targets) : EMPTY_VALUES);
      setFormError(null);
    }
  }, [open, rule, targets]);

  const discovered = useDiscoveredMetrics(!rule && values.ruleTargetId ? values.ruleTargetId : undefined);
  const metricOptions: DiscoveredMetricDto[] = discovered.data ?? [];
  const selectedMetric = metricOptions.find((m) => m.key === values.metricKey);

  const targetsOfKind = useMemo(() => targets.filter((t) => t.kind === values.kind), [targets, values.kind]);

  function update(name: keyof RuleFormValues, value: string) {
    setValues((v) => {
      if (name === 'kind') return { ...v, kind: value, ruleTargetId: '', metricKey: '' };
      if (name === 'ruleTargetId') return { ...v, ruleTargetId: value, metricKey: '' };
      return { ...v, [name]: value };
    });
  }

  const identityFields: FormFieldConfig<RuleFormValues>[] = rule
    ? []
    : [
        {
          name: 'kind',
          label: 'Kind',
          type: 'select',
          options: [
            { value: 'SERVER', label: 'Server' },
            { value: 'APPLICATION', label: 'Application' },
          ],
          validation: { required: true },
        },
        {
          name: 'ruleTargetId',
          label: 'Target',
          type: 'select',
          options: targetsOfKind.map((t) => ({ value: t.id, label: t.name })),
          hint: !values.kind ? 'Pick a kind first.' : targetsOfKind.length === 0 ? 'No targets of this kind yet.' : undefined,
          validation: { required: true },
        },
        {
          name: 'metricKey',
          label: 'Metric',
          type: 'select',
          options: metricOptions.map((m) => ({
            value: m.key,
            label: m.labels && Object.keys(m.labels).length > 0
              ? `${m.source} (${Object.entries(m.labels).map(([k, v]) => `${k}=${v}`).join(', ')})`
              : m.source,
          })),
          hint: !values.ruleTargetId
            ? 'Pick a target first.'
            : discovered.isFetching
              ? 'Reading live metrics…'
              : discovered.isError
                ? 'Could not reach this target’s metrics endpoint.'
                : metricOptions.length === 0
                  ? 'No metrics found.'
                  : undefined,
          validation: { required: true },
        },
      ];

  const fields: FormFieldConfig<RuleFormValues>[] = [
    ...identityFields,
    { name: 'name', label: 'Name (optional)', validation: { maxLength: 200 } },
    {
      name: 'operator',
      label: 'Operator',
      type: 'select',
      options: OPERATORS.map((op) => ({ value: op, label: op })),
      validation: { required: true },
    },
    { name: 'threshold', label: 'Threshold', type: 'number', validation: { required: true } },
    { name: 'consecutive', label: 'Consecutive breaches', type: 'number', validation: { required: true, min: 1 } },
    {
      name: 'severity',
      label: 'Severity',
      type: 'select',
      options: SEVERITIES.map((s) => ({ value: s, label: s === 'critical' ? 'Critical' : 'Warning' })),
      validation: { required: true },
    },
    { name: 'remindEveryMinutes', label: 'Remind every (minutes, optional)', type: 'number', validation: { min: 0 } },
    {
      name: 'enabled',
      label: 'Enabled',
      type: 'select',
      options: [
        { value: 'true', label: 'Enabled' },
        { value: 'false', label: 'Disabled' },
      ],
      validation: { required: true },
    },
  ];

  async function handleSubmit() {
    setBusy(true);
    setFormError(null);
    try {
      const shared = {
        ...(values.name.trim() && { name: values.name.trim() }),
        operator: values.operator as Operator,
        threshold: Number(values.threshold),
        consecutive: Number(values.consecutive),
        severity: values.severity as Severity,
        ...(values.remindEveryMinutes.trim() && { remindEveryMinutes: Number(values.remindEveryMinutes) }),
        enabled: values.enabled === 'true',
      };

      if (rule) {
        await onSubmit(shared satisfies UpdateRuleDefinitionDto);
      } else {
        if (!selectedMetric) throw new Error('Pick a metric.');
        await onSubmit({ ...shared, ruleTargetId: values.ruleTargetId, metric: selectedMetric } satisfies CreateRuleDefinitionDto);
      }
      onClose();
    } catch (error) {
      setFormError(error instanceof ApiError ? error.message : (error as Error).message || 'Could not save this rule.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <PopupTemplate
      open={open}
      onClose={onClose}
      title={rule ? 'Edit rule' : 'Add rule'}
      description={rule ? `${rule.ruleTargetName} · ${rule.metricLabel}` : undefined}
      size="lg"
    >
      <FormBuilder
        fields={fields}
        values={values}
        onChange={update}
        onSubmit={handleSubmit}
        formError={formError}
        busy={busy}
        onCancel={onClose}
      />
    </PopupTemplate>
  );
}
